import { getSearchParams } from '@wix/package-picker-shared';
import {
  EclipsePackagePickerSteps,
  useAnonymous,
  useDomainIntent,
  useSwitchFlows,
} from '@wix/package-picker-shared-eclipse';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { useDealerIntent } from '../useDealerIntent';

export const EclipseTitleKeys = {
  DEFAULT: 'premium-purchase-plan.header.default.title',
  UPGRADE: 'premium-purchase-plan.header.second_upgrade.title',
  DOWNGRADE: 'change_plan.app_title',
  DOMAIN: 'premium-purchase-plan.header.domain.title',
  DOMAIN_PURCHASE_INTENT:
    'premium-purchase-plan.header.domain-purchase-intent.title',
  STOREFRONT_MOBILE:
    'premium-purchase-plan.storefront_mobile.package_picker.header',
  ANONYMOUS: 'premium-purchase-plan.anonymous.header.title',
  CYCLES_PAGE: 'premium-purchase-plan.select_cycle.title',
  CHECKOUT_PAGE: 'checkout.title',
} as const;

export type EclipseTitleKey =
  (typeof EclipseTitleKeys)[keyof typeof EclipseTitleKeys];

export const useTitleKey = () => {
  const step = EclipsePackagePickerModule.internals.hooks.useCurrentStep();
  const { showDomain, shouldShowDomainNameInTitle } = useDomainIntent();
  const { isUpgradeFlow, isLoading: isSwitchLoading } = useSwitchFlows();
  const { isAnonymous } = useAnonymous();
  const { isDowngradeEnabled } = getSearchParams();
  const { title: dealerTitle, isLoading: isDealerLoading } = useDealerIntent({
    isUpgradeFlow,
  });

  const selectedProductId =
    EclipsePackagePickerModule.SDK.PackagePickerStore.useSelectedProductId();

  const isCycleStep = step === EclipsePackagePickerSteps.SELECT_CYCLE;

  const { data: product, isLoading: isProductLoading } =
    EclipsePackagePickerModule.SDK.eclipseOfferingApiClient.hooks.useFetchProduct(
      { productId: selectedProductId ?? '' },
      Boolean(selectedProductId && isCycleStep),
    );

  const getPlanStepTitleKey = (): string => {
    if (isDowngradeEnabled) {
      return EclipseTitleKeys.DOWNGRADE;
    }
    if (shouldShowDomainNameInTitle) {
      return EclipseTitleKeys.DOMAIN_PURCHASE_INTENT;
    }
    if (showDomain) {
      return EclipseTitleKeys.DOMAIN;
    }
    if (dealerTitle) {
      return dealerTitle;
    }
    if (isAnonymous) {
      return EclipseTitleKeys.ANONYMOUS;
    }
    return isUpgradeFlow ? EclipseTitleKeys.UPGRADE : EclipseTitleKeys.DEFAULT;
  };

  const getTitleKey = (): string => {
    switch (step) {
      case EclipsePackagePickerSteps.SELECT_CYCLE:
        return EclipseTitleKeys.CYCLES_PAGE;
      case EclipsePackagePickerSteps.CHECKOUT:
        return EclipseTitleKeys.CHECKOUT_PAGE;
      case EclipsePackagePickerSteps.SELECT_PLAN:
      default:
        return getPlanStepTitleKey();
    }
  };

  const getTitleParams = (): Record<string, string> | undefined => {
    if (isCycleStep && product?.name) {
      return { planName: product.name };
    }
    return undefined;
  };

  return {
    key: getTitleKey(),
    params: getTitleParams(),
    isLoading:
      isSwitchLoading || isDealerLoading || (isCycleStep && isProductLoading),
  };
};
